import { useCallback, useEffect, useState } from 'react';
import { resolveCountryOfOrigin, type CountryOfOrigin } from '@peekapak/country-origin';
import { dataResidencyManager, SupportedDataResidency } from '../../DataResidencyManager';

export interface HomeCountryResolutionState {
  /** The resolved country of origin, or null until the first resolution lands. */
  resolution: CountryOfOrigin | null;
  isResolving: boolean;
  /**
   * Re-run resolution. Called after the user persists or clears an explicit
   * choice (group 6) so the control reflects the new top-precedence signal,
   * and when the control is opened, so detection gets another chance (D5:
   * demand-driven geolocation). Re-resolution never writes to storage.
   */
  refresh: () => void;
}

/**
 * Resolve the visitor's country of origin for the home-country control. Runs on
 * mount and exposes a `refresh` for re-resolution after an explicit choice
 * changes or when the control is opened (D5: opening is the demand signal for
 * another detection attempt). `mayPromptForGeolocation` is passed through: auth
 * screens (sign-up/log-in) pass `true`, so the browser prompt is allowed when no
 * higher-precedence signal exists (D14/D5).
 */
export function useHomeCountryResolution(mayPromptForGeolocation: boolean): HomeCountryResolutionState {
  const [resolution, setResolution] = useState<CountryOfOrigin | null>(null);
  const [isResolving, setIsResolving] = useState(true);
  // Bumping this re-runs the resolving effect (used by refresh()).
  const [resolveToken, setResolveToken] = useState(0);

  const refresh = useCallback(() => setResolveToken((token) => token + 1), []);

  useEffect(() => {
    // resolveToken is purely a re-run trigger, bumped by refresh().
    void resolveToken;
    let active = true;
    setIsResolving(true);
    resolveCountryOfOrigin({ mayPromptForGeolocation })
      .then((result) => {
        if (active) {
          setResolution(result);
          // Keep the live manager (X-Backend-Region, api/api-fra host, Cognito
          // config) in step with the resolution, so the session routes where
          // the control says it will. Persistence requires authority: only an
          // explicit choice is stored; detections update in-memory state only
          // (the read path prefers a stored authoritative value, so a guess
          // can never override a recorded account region). Clearing the stored
          // residency happens at the dialog's "use detected location" action,
          // never here: a re-resolution carries no authority over storage, and
          // refresh() also runs on mere dialog-open (D5), which must not wipe
          // the residency recorded at a previous sign-in.
          if (result.source === 'explicit') {
            dataResidencyManager.persistDataResidency(result.dataResidency as SupportedDataResidency);
          } else {
            dataResidencyManager.setDataResidency(result.dataResidency as SupportedDataResidency);
          }
        }
      })
      .catch((error) => {
        console.warn('[HomeCountryControl] country-of-origin resolution failed:', error);
      })
      .finally(() => {
        if (active) {
          setIsResolving(false);
        }
      });
    return () => {
      active = false;
    };
  }, [mayPromptForGeolocation, resolveToken]);

  return { resolution, isResolving, refresh };
}
